import { css } from "styled-components/macro";

/**
 * Standard focus-visible outline for interactive elements.
 * Uses an outset offset (2px) — good for buttons, links, and most controls.
 */
export const focusVisible = css`
  &:focus-visible {
    outline: 2px solid var(--primary-cta);
    outline-offset: 2px;
  }
`;

/**
 * Inset focus-visible outline for elements with rounded borders or padding
 * where an outset outline would look detached (e.g. menu items with border-radius).
 */
export const focusVisibleInset = css`
  &:focus-visible {
    outline: 2px solid var(--primary-cta);
    outline-offset: -2px;
  }
`;

/**
 * Focus outline properties only (no selector wrapping).
 * Use when you need a custom selector, e.g. `input:focus-visible + .slider`.
 */
export const focusOutline = css`
  outline: 2px solid var(--primary-cta);
  outline-offset: 2px;
`;

/**
 * Reset browser default button styles. Apply to <button> elements that
 * need custom styling (toolbar actions, icon buttons, etc.).
 */
export const buttonReset = css`
  appearance: none;
  background: none;
  border: none;
  padding: 0;
  font: inherit;
  color: inherit;
  cursor: pointer;
`;

/**
 * Focus outline and border styles for react-select control.
 * Spread into the `control` style function: `...selectFocusStyle(state)`
 */
export const selectFocusStyle = (state) => ({
  outline: state.isFocused ? "2px solid var(--primary-cta)" : "none",
  outlineOffset: "2px",
  boxShadow: "none",
  ...(state.isFocused ? { borderColor: "var(--primary-cta)" } : {}),
});

/**
 * Shared react-select theme that maps CSS custom properties to react-select's
 * internal color tokens. Pass as the `theme` prop on any `<Select>`.
 *
 * @param {Object} [overrides] - Override individual color tokens.
 * @returns {(theme: Object) => Object}
 */
export const createSelectTheme =
  (overrides = {}) =>
  (theme) => ({
    ...theme,
    colors: {
      ...theme.colors,
      neutral0: "var(--menu-color)",
      neutral20: "var(--grey-0)",
      neutral30: "var(--grey-0)",
      primary: "var(--primary-cta)",
      primary25: "var(--menu-color-hover)",
      primary50: "var(--menu-color-active)",
      ...overrides,
    },
  });

/**
 * Shared react-select style object with focus styles, hidden indicator separator,
 * and rounded menus. Pass as the `styles` prop on any `<Select>`.
 *
 * @param {Object} [overrides] - Per-key style function overrides merged on top.
 * @returns {Object} react-select styles object
 */
export const createSelectStyles = (overrides = {}) => ({
  control: (base, state) => ({
    ...base,
    border: `2px solid ${state.isFocused ? "var(--primary-cta)" : "var(--grey-0)"}`,
    display: "flex",
    borderRadius: "5px",
    cursor: "pointer",
    ...selectFocusStyle(state),
  }),
  singleValue: (base) => ({
    ...base,
    color: "var(--grey-0)",
  }),
  indicatorsContainer: (base) => ({
    ...base,
    paddingTop: 2,
  }),
  indicatorSeparator: (base) => ({
    ...base,
    display: "none",
  }),
  option: (base, state) => ({
    ...base,
    color: state.isSelected ? "white" : "var(--grey-0)",
  }),
  menu: (base) => ({
    ...base,
    borderRadius: "10px",
    overflow: "hidden",
    paddingTop: "0px",
    boxShadow: "var(--default-drop-shadow)",
  }),
  menuList: (base) => ({
    ...base,
    paddingTop: "0px",
    paddingBottom: "0px",
  }),
  ...overrides,
});
